import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
  Card,
  Row,
  Col
} from 'react-bootstrap';
import {
  IconBug,
  IconMessageCircle,
  IconCopy,
  IconRefresh,
  IconUser,
  IconRobot,
  IconAlertTriangle,
  IconX,
  IconClock,
  IconHash
} from '@tabler/icons-react';
import useDebugAnalysis from '../hooks/useDebugAnalysis';

const MessageDebugModal = ({ 
  show, 
  onHide, 
  targetMessage = null,
  previousMessage = null,
  messageIndex = 0,
  agentContext = null 
}) => {
  // Estados locais
  const [customPrompt, setCustomPrompt] = useState('');
  const [analysisResult, setAnalysisResult] = useState('');
  const [copied, setCopied] = useState(false);

  // Hook de debug
  const { 
    loading, 
    error, 
    resetError,
    analyzeMessage,
    defaultPrompts 
  } = useDebugAnalysis();

  // Reset ao fechar
  useEffect(() => {
    if (!show) {
      setAnalysisResult('');
      setCustomPrompt('');
      setCopied(false);
      resetError();
    }
  }, [show, resetError]);

  // Set prompt padrão quando abre
  useEffect(() => {
    if (show && !customPrompt && defaultPrompts.message && targetMessage) {
      // Criar prompt contextualizado
      const userText = previousMessage?.text || 'Início da conversa';
      const agentResponse = targetMessage.text;
      
      const contextualPrompt = `Analise esta interação específica:

ENTRADA DO USUÁRIO: "${userText.substring(0, 200)}${userText.length > 200 ? '...' : ''}"

RESPOSTA DO AGENTE: "${agentResponse.substring(0, 200)}${agentResponse.length > 200 ? '...' : ''}"

Por que o agente respondeu dessa forma? A resposta faz sentido? Há algo que poderia ser melhorado?`;
      
      setCustomPrompt(contextualPrompt);
    }
  }, [show, customPrompt, defaultPrompts, targetMessage, previousMessage]);

  // Executar análise
  const handleAnalyze = async () => {
    if (!targetMessage) {
      setAnalysisResult('❌ Nenhuma mensagem selecionada para debug.');
      return;
    }

    const userMessage = previousMessage?.text || 'Início da conversa';
    const agentResponse = targetMessage.text;

    const result = await analyzeMessage(userMessage, agentResponse, customPrompt);

    if (result.success) {
      setAnalysisResult(result.analysis);
    } else {
      setAnalysisResult(`❌ Erro na análise: ${result.error || 'Erro inesperado'}`);
    }
  };

  // Copiar resultado
  const handleCopy = () => {
    navigator.clipboard.writeText(analysisResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Informações da mensagem
  const getMessageInfo = () => {
    if (!targetMessage) return null;

    return {
      timestamp: targetMessage.timestamp,
      length: targetMessage.text?.length || 0,
      isError: targetMessage.isError,
      isAudio: targetMessage.isAudio,
      isPartOf: targetMessage.isPartOf,
      messageType: targetMessage.messageType || 'text'
    };
  };

  const messageInfo = getMessageInfo();

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="lg"
      centered
      className="debug-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <IconBug size={20} />
          Análise da Mensagem #{messageIndex + 1}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {targetMessage ? (
          <>
            {/* Info da Mensagem */}
            <Row className="mb-3">
              <Col md={6}>
                <div className="debug-stat-item">
                  <div className="debug-stat-value">
                    <IconHash size={16} className="me-1" />
                    {messageIndex + 1}
                  </div>
                  <div className="debug-stat-label">Posição</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="debug-stat-item">
                  <div className="debug-stat-value">
                    <IconClock size={16} className="me-1" />
                    {messageInfo.timestamp}
                  </div>
                  <div className="debug-stat-label">Horário</div>
                </div>
              </Col>
            </Row>

           

            {/* Contexto da Conversa */}
            <div className="mb-3">
              <h6 className="d-flex align-items-center gap-2 mb-2">
                <IconMessageCircle size={16} />
                Contexto da Interação
              </h6>
              
              {/* Mensagem Anterior */}
              {previousMessage ? (
                <Card className="mb-2 debug-context-card debug-user-message">
                  <Card.Body style={{ padding: '10px 12px' }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <IconUser size={14} />
                      <small className="fw-bold">Usuário disse:</small>
                      <small className="text-muted">{previousMessage.timestamp}</small>
                    </div>
                    <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                      {previousMessage.isAudio ? (
                        <em className="text-muted">🎤 Mensagem de áudio</em>
                      ) : (
                        previousMessage.text
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <Alert variant="info" className="py-2">
                  <small><IconAlertTriangle size={12} className="me-1" />Esta é a primeira mensagem da conversa</small>
                </Alert>
              )}

              {/* Mensagem Alvo */}
              <Card className={`debug-context-card ${targetMessage.isError ? 'debug-error-message' : 'debug-agent-message'}`}>
                <Card.Body style={{ padding: '10px 12px' }}>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <IconRobot size={14} />
                    <small className="fw-bold">Agente respondeu:</small>
                    <small className="text-muted">{targetMessage.timestamp}</small>
                    {targetMessage.isError && <IconAlertTriangle size={12} className="text-danger" />}
                  </div>
                  <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                    {targetMessage.isAudio ? (
                      <em className="text-muted">🎤 Mensagem de áudio</em>
                    ) : (
                      targetMessage.text
                    )}
                  </div>
                </Card.Body>
              </Card>
            </div>

     

            {/* Botões de Análise */}
            <div className="d-flex gap-2 mb-3">
              <Button 
                variant="primary" 
                onClick={handleAnalyze}
                disabled={loading || !customPrompt.trim()}
                className="d-flex align-items-center gap-2"
              >
                {loading ? <Spinner size="sm" /> : <IconBug size={16} />}
                {loading ? 'Analisando...' : 'Analisar Mensagem'}
              </Button>
              
             
            </div>

            {/* Error Display */}
            {error && (
              <Alert variant="danger" className="d-flex align-items-center gap-2">
                <IconAlertTriangle size={16} />
                {error}
              </Alert>
            )}

            {/* Resultado */}
            {analysisResult && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Resultado da Análise</h6>
                  <Button
                    variant={copied ? "success" : "outline-secondary"}
                    size="sm"
                    onClick={handleCopy}
                    className={`debug-copy-btn ${copied ? 'copied' : ''}`}
                  >
                    {copied ? '✓ Copiado' : <IconCopy size={14} />}
                  </Button>
                </div>
                <div 
                  className="debug-analysis-result p-3"
                  style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {analysisResult}
                </div>
              </div>
            )}
          </>
        ) : (
          <Alert variant="warning">
            <IconAlertTriangle size={16} className="me-2" />
            Nenhuma mensagem selecionada para análise.
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MessageDebugModal;